import type { z } from 'zod'
import { AUTH_ROUTES } from '../constants/auth.routes.ts'
import {
  type ProviderLoginResponse,
  providerLoginResponseSchema,
  type RefreshSessionResponse,
  refreshSessionResponseSchema,
  type SimpleMessageResponse,
  simpleMessageResponseSchema,
  type ValidateRecoveryCodeResponse,
  validateRecoveryCodeResponseSchema,
} from '../constants/auth.schemas.ts'

export interface ApiRequestConfig<T> {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  data?: unknown
  schema: z.ZodSchema<T>
  accessToken?: string
}

type Requester = <T>(config: ApiRequestConfig<T>) => Promise<T>

interface ProviderLoginInput {
  credentialType: 'id_token' | 'authorization_code'
  credential: string
  nonce?: string
}

interface RegisterInput {
  name: string
  phone: string
  email: string | null
  password: string
}

interface PhoneLoginInput {
  phone: string
  password: string
}

interface PasswordRecoveryInput {
  phone: string
}

interface ValidateRecoveryCodeInput {
  phone: string
  recoveryCode: string
}

interface ResetPasswordInput extends ValidateRecoveryCodeInput {
  newPassword: string
  confirmNewPassword: string
}

async function defaultRequest<T>(config: ApiRequestConfig<T>): Promise<T> {
  const api = await import('../../../services/api.ts')
  return api.request(config)
}

export function createAuthApi(requester: Requester = defaultRequest) {
  return {
    register(input: RegisterInput): Promise<ProviderLoginResponse> {
      return requester({
        url: AUTH_ROUTES.REGISTER,
        method: 'POST',
        data: input,
        schema: providerLoginResponseSchema,
      })
    },

    loginWithPhone(input: PhoneLoginInput): Promise<ProviderLoginResponse> {
      return requester({
        url: AUTH_ROUTES.LOGIN_PHONE,
        method: 'POST',
        data: input,
        schema: providerLoginResponseSchema,
      })
    },

    loginWithProvider(
      provider: 'google',
      input: ProviderLoginInput,
    ): Promise<ProviderLoginResponse> {
      return requester({
        url: provider === 'google' ? AUTH_ROUTES.LOGIN_GOOGLE : AUTH_ROUTES.LOGIN_GOOGLE,
        method: 'POST',
        data: {
          credential_type: input.credentialType,
          credential: input.credential,
          ...(input.nonce ? { nonce: input.nonce } : {}),
        },
        schema: providerLoginResponseSchema,
      })
    },

    startPasswordRecovery(input: PasswordRecoveryInput): Promise<SimpleMessageResponse> {
      return requester({
        url: AUTH_ROUTES.RECOVERY_PASSWORD,
        method: 'POST',
        data: input,
        schema: simpleMessageResponseSchema,
      })
    },

    resendPasswordRecovery(input: PasswordRecoveryInput): Promise<SimpleMessageResponse> {
      return requester({
        url: AUTH_ROUTES.RESEND_RECOVERY_CODE,
        method: 'POST',
        data: input,
        schema: simpleMessageResponseSchema,
      })
    },

    validateRecoveryCode(input: ValidateRecoveryCodeInput): Promise<ValidateRecoveryCodeResponse> {
      return requester({
        url: AUTH_ROUTES.VALIDATE_RECOVERY_CODE,
        method: 'POST',
        data: input,
        schema: validateRecoveryCodeResponseSchema,
      })
    },

    resetPassword(input: ResetPasswordInput): Promise<SimpleMessageResponse> {
      return requester({
        url: AUTH_ROUTES.RESET_PASSWORD,
        method: 'POST',
        data: input,
        schema: simpleMessageResponseSchema,
      })
    },

    refreshSession(): Promise<RefreshSessionResponse> {
      return requester({
        url: AUTH_ROUTES.REFRESH_TOKEN,
        method: 'POST',
        schema: refreshSessionResponseSchema,
      })
    },

    logout(accessToken: string): Promise<SimpleMessageResponse> {
      return requester({
        url: AUTH_ROUTES.LOGOUT,
        method: 'POST',
        accessToken,
        schema: simpleMessageResponseSchema,
      })
    },
  }
}

export const authApi = createAuthApi()
